import React, { useState, useEffect } from "react";
import { FaTimes, FaBox } from "react-icons/fa";
import CustomSelect from '../customComponents/CustomSelect';
import styles from "../styles/ModalProducts.module.css";
import axios from 'axios';
import { API_URL } from '../config/api';


export default function ModalProducts({ open, onClose, warehouseId, stocks = true, onAddProducts }) {
  const [products, setProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTab, setActiveTab] = useState("nomenclature"); 
  const [filter, setFilter] = useState({ search: "" });
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);

  const token = localStorage.getItem("token");

  const resetModal = () => {
    setProducts([]);
    setSelectedItems([]);
    setActiveTab("nomenclature");
    setFilter({ search: "" });
    setSelectedCategory(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const catRes = await axios.get(`${API_URL}/api/product_categories`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const catOptions = (catRes.data.data || []).map(c => ({ value: c.id, label: c.title }));
        setCategories(catOptions);
      } catch (err) {
        console.error("Ошибка при загрузке категорий:", err);
      }
    };
    fetchCategories();
  }, [token]);

  useEffect(() => {
    if (warehouseId) {
      setSelectedWarehouse({ value: warehouseId, label: `Склад #${warehouseId}` });
    }
  }, [warehouseId]);

  useEffect(() => {
    if (!open) return;
    if (stocks && !warehouseId) return;

    const fetchProducts = async () => {
      try {
        let res;
        let data = [];

        if (stocks) {
          // 🏬 ИЗ СКЛАДА
          let url = `${API_URL}/api/stocks?storage_id=${warehouseId}`;
          if (selectedCategory) url += `&category_id=${selectedCategory.value}`;

          res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
          data = (res.data?.data || []).map(item => ({
            id: item.product_id,
            name: item.name_ru,
            article: item.article || item.product_id,
            barcode: item.ntin || "",
            qty: Number(item.quantity) || 0,
            price: Number(item.sell_price || item.price) || 0,
            purchase_price: Number(item.purchase_price || 0),
            profit_percentage: parseFloat(item.profit_percentage) || 0,
            category_id: item.category_id || null,
            fromStock: true
          }));
        } else {
          // 📦 ИЗ НОМЕНКЛАТУРЫ
          res = await axios.get(`${API_URL}/api/products`, { headers: { Authorization: `Bearer ${token}` } });

          data = (res.data?.data || []).map(item => {
            const mapped = {
              id: item.id,
              name: item.name_ru,
              article: item.article,
              barcode: item.ntin || "",
              qty: 0,
              price: Number(item.sell_price) || 0,
              purchase_price: Number(item.purchase_price) || 0,  // ✅ передаём
              profit_percentage: parseFloat(item.profit_percentage) || 0,  // ✅ передаём
              category_id: item.category_id,
              fromStock: false
            };
            console.log("Mapped product:", mapped);
            return mapped;
          });
        }

        console.log("Products loaded:", data);
        setProducts(data);
        setSelectedItems([]);
      } catch (err) {
        console.error("Ошибка загрузки товаров:", err);
        setProducts([]);
      }
    };

    fetchProducts();
  }, [open, warehouseId, stocks, selectedCategory, token]);

  const handleCheckbox = (item) => {
    if (selectedItems.includes(item.id)) {
      setSelectedItems(selectedItems.filter((id) => id !== item.id));
    } else {
      setSelectedItems([...selectedItems, item.id]);
    }
  };

  const filteredData = products.filter((item) => {
    const matchesSearch =
      !filter.search ||
      item.name.toLowerCase().includes(filter.search.toLowerCase()) ||
      item.barcode.includes(filter.search) ||
      item.article.includes(filter.search);

    if (activeTab === "selected") {
      return selectedItems.includes(item.id) && matchesSearch;
    }

    return matchesSearch;
  });

  const handleAddSelected = () => {
    const selectedProducts = selectedItems.map((id) => products.find((i) => i.id === id));
    console.log("Selected products to add:", selectedProducts);
    if (selectedProducts.length > 0 && typeof onAddProducts === "function") {
      onAddProducts(selectedProducts.map(p => ({
        ...p,
        purchase_price: Number(p.purchase_price || 0),
        profit_percentage: Number(p.profit_percentage || 0)
      })));
    }
    handleClose();
  };

  if (!open) return null;

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modalContent}>
        {/* Tabs Header */}
        <div className={styles.tabsHeader}>
          <div
            className={`${styles.tab} ${activeTab === "nomenclature" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("nomenclature")}
          >
            Номенклатура
          </div>
          <div
            className={`${styles.tab} ${
              activeTab === "selected" && selectedItems.length > 0 ? styles.activeTab : ""
            } ${selectedItems.length === 0 ? styles.disabledTab : ""}`}
            onClick={() => selectedItems.length > 0 && setActiveTab("selected")}
          >
            Выбрано позиций: {selectedItems.length}
          </div>
          <button className={styles.closeBtn} onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.filtersTop}>
            <CustomSelect 
              className={styles.filters__select} 
              options={[selectedWarehouse]} 
              value={selectedWarehouse} 
              onChange={() => {}} 
              placeholder="Склад" 
              isDisabled={true} 
            />
            <CustomSelect 
              className={styles.filters__select} 
              options={categories} 
              value={selectedCategory} 
              onChange={(cat) => {
                  setSelectedCategory(cat);
                  const categoryId = cat?.value ?? cat;
                  console.log("Выбрана категория ID:", categoryId);
              }} 
              placeholder="Категория" 
            />
          </div>

          <div className={styles.filtersBottom}>
            <input
              type="text"
              placeholder="Поиск по штрихкоду"
              value={filter.search}
              className={styles.input}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            />
            <input
              type="text"
              placeholder="Поиск по имени"
              value={filter.search}
              className={styles.input}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            />
            <input
              type="text"
              placeholder="Поиск по артиклу"
              value={filter.search}
              className={styles.input}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            />
            <input type="date" name="date" className={styles.input} />
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableContainer}>
          <table className={styles.modalTable}>
            <thead>
              <tr>
                <th>
                  <label className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      checked={filteredData.length > 0 && filteredData.every((item) => selectedItems.includes(item.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const allIds = filteredData.map((item) => item.id);
                          setSelectedItems([...new Set([...selectedItems, ...allIds])]);
                        } else {
                          const remaining = selectedItems.filter((id) => !filteredData.some((item) => item.id === id));
                          setSelectedItems(remaining);
                        }
                      }}
                    />
                    <span className={styles.customCheckbox}></span>
                  </label>
                </th>
                <th>Фото</th>
                <th>Название</th>
                <th>Артикул</th>
                <th>Штрихкод</th>
                <th>Количество</th>
                <th>Цена продажи</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <tr key={item.id}>
                  <td>
                    <label className={styles.checkboxContainer}>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => handleCheckbox(item)}
                      />
                      <span className={styles.customCheckbox}></span>
                    </label>
                  </td>
                  <td><FaBox /></td>
                  <td>{item.name}</td>
                  <td>{item.article}</td>
                  <td>{item.barcode}</td>
                  <td>{item.qty}</td>
                  <td>{item.price}</td>
                </tr>
              ))}
              {filteredData.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "20px" }}>
                    Нет товаров для отображения
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={handleClose}>
            Отмена
          </button>
          <button
            className={styles.addBtn}
            onClick={handleAddSelected}
          >
            Добавить выбранные
          </button>
        </div>
      </div>
    </div>
  );
}
